<script lang="ts" setup>
	import {
		addOrderLineToShoppingCart,
		type BaseOrderLine,
		type BaseProduct,
		createOrderLine,
		getPackingUnit,
		getRoundedValue,
		ignoreKeyPressIfNotInteger,
		removeOrderLineFromShoppingCart,
		shoppingCartStorage,
		updateOrderLineAmount
	} from 'dummen-shared';
	import {computed} from 'vue';

	const props = defineProps<{
		week: number;
		year: number;
		region: string;
		product: BaseProduct;
		available: number;
	}>();

	const {name, patcode, form, packingUnitPerCompanyId} = props.product;

	const orderLines = computed(() => {
		return shoppingCartStorage.value.orderLines;
	});

	const packingUnit = computed(() => getPackingUnit(packingUnitPerCompanyId));

	const orderLine = computed(() => {
		return orderLines.value.find((line: BaseOrderLine) => {
			return line &&
				line.articleId === patcode &&
				line.week === props.week &&
				line.year === props.year &&
				line.formId === form?.patId;
		});
	});

	const hasNotExistingVariety = computed(() => orderLine.value?.notExistingVariety);

	const orderLinesOfSameVariety = computed(() => {
		return orderLines.value.filter((line: BaseOrderLine) => {
			return line &&
				line.articleId === patcode &&
				line.articleName === name &&
				line.week === props.week &&
				line.year === props.year &&
				line.formId === form?.patId;
		});
	});

	const hasMultipleVarieties = computed(() => orderLinesOfSameVariety.value?.length > 1);

	const sumOfAllVarieties = computed(() => {
		if (!orderLinesOfSameVariety.value) {
			return 0;
		}

		return orderLinesOfSameVariety.value
			.map((line: BaseOrderLine) => line.amount)
			.reduce((prev: number, next: number) => prev + next);
	});

	const invalid = computed(() => {
		if (hasMultipleVarieties.value) {
			return sumOfAllVarieties.value > props.available;
		}

		return false;
	});

	const amount = computed({
		get() {
			if (hasMultipleVarieties.value) {
				return sumOfAllVarieties.value;
			}

			return orderLine.value?.amount ?? null;
		},

		set(newAmount) {
			const existingOrderLine = orderLine.value;

			if (existingOrderLine && (newAmount === null || newAmount < 1)) {
				removeOrderLineFromShoppingCart(existingOrderLine);
				return;
			}

			// If the order line should be created or updated, first check if the desired amount
			// is a valid integer. If not return, so that the input will reset to the last valid amount.
			if (!newAmount || !Number.isInteger(newAmount)) {
				return;
			}

			// At this point, the existing order line should be updated.
			if (existingOrderLine) {
				updateOrderLineAmount(existingOrderLine, newAmount);
				return;
			}

			const newOrderLine = createOrderLine(props.week, props.year, props.region, props.product, newAmount);
			addOrderLineToShoppingCart(newOrderLine);
		}
	});

	const getNearestValueDown = computed(() => {
		if (!amount.value) {
			return 0;
		}

		return Math.floor(amount.value / 100) * 100;
	});

	const getNearestValueUp = computed(() => getNearestValueDown.value + packingUnit.value);

	const isNotMultipleOfStepAmount = computed(() => {
		if (!amount.value) {
			return 0;
		}

		return (amount.value % packingUnit.value) !== 0;
	});

	const getCustomTooltipContent = computed(() => {
		if (hasMultipleVarieties.value && sumOfAllVarieties.value > props.available) {
			return `The sum of the same variety over all consignments (${sumOfAllVarieties.value}) exceeds the available amount of (${props.available})`;
		}

		if (hasMultipleVarieties.value) {
			return 'Please use the shopping cart to edit your order';
		}

		return null;
	});

	const tooltipContent = computed(() => {
		if (!amount.value || amount.value === 0) {
			return null;
		}

		if (props.available < 1) {
			return 'Variety is not available for this week';
		}

		if (getCustomTooltipContent.value !== null) {
			return getCustomTooltipContent;
		}

		if (amount.value > props.available) {
			return `Value must be less than or equal to ${props.available}`;
		}

		if (amount.value < 0) {
			return 'Value must be greater then 0';
		}

		if (isNotMultipleOfStepAmount.value) {
			return `Please enter a valid value. The two nearest valid values are ${getNearestValueDown.value} and ${getNearestValueUp.value}`;
		}

		return null;
	});

	const setRoundedValue = (event: FocusEvent) => {
		const el = event.target as HTMLInputElement;

		if (!el.value) {
			return;
		}

		amount.value = getRoundedValue(parseInt(el.value), props.available, packingUnit.value);
	};

</script>

<template>
	<form novalidate onsubmit="return false;">
		<VTooltip :disabled="!tooltipContent">

			<input v-model="amount"
			       :class="{'invalid': invalid}"
			       :disabled="available < 1 || hasNotExistingVariety"
			       :max="available"
			       :step="packingUnit"
			       class="orderinput"
			       min="0"
			       type="number"
			       @blur="setRoundedValue"
			       @keypress="ignoreKeyPressIfNotInteger($event)" />

			<template #popper>
				<div>{{ tooltipContent }}</div>
			</template>

		</VTooltip>

		<span>{{ available }}</span>
	</form>
</template>
